<template>
	<div
		class="pdf-viewer"
		@contextmenu="preventProtectedAction"
		@copy="preventProtectedAction"
		@cut="preventProtectedAction"
		@dragstart="preventProtectedAction"
		@selectstart="preventProtectedAction"
	>
		<div v-if="error" class="pdf-viewer__error">{{ error }}</div>
		<div v-else class="pdf-viewer__content">
			<div class="pdf-viewer__toolbar">
				<span class="pdf-viewer__meta">
					Страница {{ currentPage }} / {{ pagesCount || 1 }}
				</span>
				<input
					v-model="pageInput"
					type="number"
					min="1"
					:max="pagesCount || 1"
					class="pdf-viewer__page-input"
					placeholder="Стр."
					@keydown.enter.prevent="jumpToPage"
				/>
				<button
					type="button"
					class="pdf-viewer__btn"
					:disabled="loading || !pagesCount"
					@click="jumpToPage"
				>
					Перейти
				</button>
				<button
					type="button"
					class="pdf-viewer__btn"
					:disabled="loading || zoom <= PDF_ZOOM_MIN"
					@click="changeZoom(-0.05)"
				>
					-
				</button>
				<span class="pdf-viewer__meta">{{ Math.round(zoom * 100) }}%</span>
				<input
					v-model.number="zoomSlider"
					type="range"
					class="pdf-viewer__zoom-slider"
					:min="Math.round(PDF_ZOOM_MIN * 100)"
					:max="Math.round(PDF_ZOOM_MAX * 100)"
					step="1"
					:disabled="loading"
					@input="applyZoomFromSlider(false)"
					@change="applyZoomFromSlider(true)"
				/>
				<button
					type="button"
					class="pdf-viewer__btn"
					:disabled="loading || zoom >= PDF_ZOOM_MAX"
					@click="changeZoom(0.05)"
				>
					+
				</button>
			</div>

			<div class="pdf-viewer__stage" ref="stageRef" @wheel="handleStageWheel">
				<div v-if="loading && !pagesCount" class="pdf-viewer__loading">Загрузка PDF...</div>
				<div v-else class="pdf-viewer__pages" :style="pagesScaleStyle">
					<div
						v-for="pageNumber in pagesCount"
						:key="`pdf-page-${pageNumber}`"
						class="pdf-viewer__page"
						:ref="el => setPageContainerRef(el, pageNumber - 1)"
					>
						<canvas
							class="pdf-viewer__canvas"
							:ref="el => setCanvasRef(el, pageNumber - 1)"
						></canvas>
						<div class="pdf-viewer__page-index">Страница {{ pageNumber }}</div>
					</div>
				</div>
				<div
					v-if="downloadDisabled"
					class="pdf-viewer__watermark"
					aria-hidden="true"
				>
					Protected View
				</div>
			</div>
		</div>
	</div>
</template>

<script setup>
import { computed, ref, shallowRef, watch, nextTick, onBeforeUnmount } from 'vue'
import { api } from '@/services/api'

const props = defineProps({
	fileUrl: {
		type: String,
		default: '',
	},
	downloadDisabled: {
		type: Boolean,
		default: false,
	},
})

const PDF_ZOOM_MIN = 0.5
const PDF_ZOOM_MAX = 2

const stageRef = ref(null)
const loading = ref(false)
const error = ref('')
const pdfDoc = shallowRef(null)
const renderTask = shallowRef(null)
const currentPage = ref(1)
const pagesCount = ref(0)
const zoom = ref(1)
const displayZoom = ref(1)
const renderedZoom = ref(1)
const zoomSlider = ref(100)
const pageInput = ref('1')
const rendering = ref(false)
const pendingRender = ref(false)
const pageCanvases = ref([])
const pageContainers = ref([])
let pdfjsLibRef = null
let pageObserver = null
let renderDebounceTimer = null
let zoomRafId = null
let wheelZoomRafId = null
let wheelDeltaAcc = 0

const clampZoom = value => Math.min(PDF_ZOOM_MAX, Math.max(PDF_ZOOM_MIN, value))
const pagesScaleStyle = computed(() => {
	// Пока идёт перерисовка в новом масштабе, берём целевой zoom как базу,
	// чтобы избежать "двойного" масштабирования и визуальных прыжков.
	const baseZoom = (rendering.value ? zoom.value : renderedZoom.value) || 1
	return {
		zoom: String(displayZoom.value / baseZoom),
	}
})

const stopZoomAnimation = () => {
	if (!zoomRafId) return
	cancelAnimationFrame(zoomRafId)
	zoomRafId = null
}

const animateZoomToTarget = () => {
	if (zoomRafId) return
	const tick = () => {
		const target = zoom.value
		const current = displayZoom.value
		const diff = target - current
		if (Math.abs(diff) < 0.002) {
			displayZoom.value = target
			zoomRafId = null
			return
		}
		displayZoom.value = current + diff * 0.14
		zoomRafId = requestAnimationFrame(tick)
	}
	zoomRafId = requestAnimationFrame(tick)
}

const stopWheelZoomLoop = () => {
	if (!wheelZoomRafId) return
	cancelAnimationFrame(wheelZoomRafId)
	wheelZoomRafId = null
}

const runWheelZoomLoop = () => {
	if (wheelZoomRafId) return
	const tick = () => {
		const delta = wheelDeltaAcc
		wheelDeltaAcc = 0

		if (Math.abs(delta) > 0.01) {
			// Экспоненциальный коэффициент даёт более естественный zoom на разных устройствах.
			const factor = Math.exp((-delta * 0.0015))
			const nextZoom = clampZoom(zoom.value * factor)
			if (nextZoom !== zoom.value) {
				zoom.value = nextZoom
				zoomSlider.value = Math.round(nextZoom * 100)
				animateZoomToTarget()
			}
		}

		if (Math.abs(wheelDeltaAcc) > 0.01) {
			wheelZoomRafId = requestAnimationFrame(tick)
			return
		}
		wheelZoomRafId = null
	}
	wheelZoomRafId = requestAnimationFrame(tick)
}

const isSameOrigin = url => {
	try {
		const parsed = new URL(url, window.location.href)
		return parsed.origin === window.location.origin
	} catch {
		return true
	}
}

const ensurePdfJs = () =>
	new Promise((resolve, reject) => {
		if (pdfjsLibRef) {
			resolve(pdfjsLibRef)
			return
		}

		if (typeof window !== 'undefined' && window.pdfjsLib) {
			pdfjsLibRef = window.pdfjsLib
			if (pdfjsLibRef.GlobalWorkerOptions) {
				pdfjsLibRef.GlobalWorkerOptions.workerSrc =
					'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js'
			}
			resolve(pdfjsLibRef)
			return
		}

		const existingScript = document.querySelector('script[data-pdfjs-cdn="1"]')
		if (existingScript) {
			existingScript.addEventListener('load', () => {
				pdfjsLibRef = window.pdfjsLib || null
				if (!pdfjsLibRef) {
					reject(new Error('pdf.js not available'))
					return
				}
				if (pdfjsLibRef.GlobalWorkerOptions) {
					pdfjsLibRef.GlobalWorkerOptions.workerSrc =
						'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js'
				}
				resolve(pdfjsLibRef)
			})
			existingScript.addEventListener('error', () => reject(new Error('Failed to load pdf.js')))
			return
		}

		const script = document.createElement('script')
		script.src = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js'
		script.async = true
		script.setAttribute('data-pdfjs-cdn', '1')
		script.onload = () => {
			pdfjsLibRef = window.pdfjsLib || null
			if (!pdfjsLibRef) {
				reject(new Error('pdf.js not available'))
				return
			}
			if (pdfjsLibRef.GlobalWorkerOptions) {
				pdfjsLibRef.GlobalWorkerOptions.workerSrc =
					'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js'
			}
			resolve(pdfjsLibRef)
		}
		script.onerror = () => reject(new Error('Failed to load pdf.js'))
		document.head.appendChild(script)
	})

const preventProtectedAction = event => {
	if (!props.downloadDisabled) return
	event.preventDefault()
	event.stopPropagation()
}

const destroyRenderTask = () => {
	if (!renderTask.value) return
	renderTask.value.cancel()
	renderTask.value = null
}

const destroyPdf = async () => {
	destroyRenderTask()
	if (!pdfDoc.value) return
	await pdfDoc.value.destroy()
	pdfDoc.value = null
}

const disconnectObserver = () => {
	if (!pageObserver) return
	pageObserver.disconnect()
	pageObserver = null
}

const setCanvasRef = (el, index) => {
	pageCanvases.value[index] = el || null
}

const setPageContainerRef = (el, index) => {
	pageContainers.value[index] = el || null
}

const scrollToPage = pageNumber => {
	const safePage = Math.min(Math.max(pageNumber, 1), pagesCount.value || 1)
	const target = pageContainers.value[safePage - 1]
	if (!target) return
	target.scrollIntoView({ behavior: 'smooth', block: 'start' })
}

const setupObserver = () => {
	disconnectObserver()
	if (!stageRef.value) return

	pageObserver = new IntersectionObserver(
		entries => {
			let best = null
			for (const entry of entries) {
				if (!entry.isIntersecting) continue
				if (!best || entry.intersectionRatio > best.intersectionRatio) best = entry
			}
			if (!best) return
			const page = Number(best.target.dataset.page || '1')
			if (!Number.isFinite(page)) return
			currentPage.value = page
			pageInput.value = String(page)
		},
		{
			root: stageRef.value,
			threshold: [0.35, 0.6, 0.85],
		}
	)

	for (let i = 0; i < pageContainers.value.length; i += 1) {
		const container = pageContainers.value[i]
		if (!container) continue
		container.dataset.page = String(i + 1)
		pageObserver.observe(container)
	}
}

const renderAllPages = async (targetZoom = zoom.value) => {
	if (!pdfDoc.value) return
	rendering.value = true
	try {
		await nextTick()
		for (let pageNumber = 1; pageNumber <= pagesCount.value; pageNumber += 1) {
			const canvas = pageCanvases.value[pageNumber - 1]
			if (!canvas) continue
			const page = await pdfDoc.value.getPage(pageNumber)
			const viewport = page.getViewport({ scale: targetZoom })
			const context = canvas.getContext('2d', { alpha: false })
			canvas.width = Math.floor(viewport.width)
			canvas.height = Math.floor(viewport.height)
			renderTask.value = page.render({
				canvasContext: context,
				viewport,
			})
			await renderTask.value.promise
			renderTask.value = null
		}
		setupObserver()
	} finally {
		rendering.value = false
	}
}

const runRenderLoop = async () => {
	if (!pdfDoc.value || rendering.value) return
	pendingRender.value = false
	const targetZoom = zoom.value
	await renderAllPages(targetZoom)
	renderedZoom.value = targetZoom
	if (pendingRender.value || zoom.value !== renderedZoom.value) {
		runRenderLoop()
	}
}

const scheduleRenderAllPages = ({ immediate = false, delay = 120 } = {}) => {
	pendingRender.value = true
	if (renderDebounceTimer) {
		clearTimeout(renderDebounceTimer)
		renderDebounceTimer = null
	}
	if (immediate) {
		runRenderLoop()
		return
	}
	renderDebounceTimer = setTimeout(() => {
		runRenderLoop()
	}, delay)
}

const loadPdf = async () => {
	error.value = ''
	loading.value = true
	pagesCount.value = 0
	pageCanvases.value = []
	pageContainers.value = []

	try {
		await destroyPdf()
		if (!props.fileUrl) {
			loading.value = false
			return
		}

		const pdfjs = await ensurePdfJs()
		let loadingTask = null

		try {
			// Для приватных /api ссылок качаем через общий api-клиент (с Authorization).
			const fileBuffer = await api.get(props.fileUrl, {
				baseURL: '',
				responseType: 'arraybuffer',
				skipAuth: !isSameOrigin(props.fileUrl),
			})
			loadingTask = pdfjs.getDocument({
				data: new Uint8Array(fileBuffer),
				disableWorker: true,
			})
		} catch {
			// Fallback: прямое чтение по URL для публичных/внешних PDF.
			loadingTask = pdfjs.getDocument({
				url: props.fileUrl,
				disableWorker: true,
				withCredentials: true,
			})
		}

		pdfDoc.value = await loadingTask.promise
		pagesCount.value = pdfDoc.value.numPages || 0
		currentPage.value = 1
		pageInput.value = '1'
		displayZoom.value = zoom.value
		renderedZoom.value = zoom.value
		pageCanvases.value = []
		pageContainers.value = []
		await renderAllPages(zoom.value)
	} catch (err) {
		error.value = err?.message || 'PDF preview is unavailable for this file.'
	} finally {
		loading.value = false
	}
}

const jumpToPage = () => {
	const parsed = Number.parseInt(String(pageInput.value || ''), 10)
	if (!Number.isFinite(parsed)) return
	const safePage = Math.min(Math.max(parsed, 1), pagesCount.value || 1)
	currentPage.value = safePage
	pageInput.value = String(safePage)
	scrollToPage(safePage)
}

const changeZoom = delta => {
	const nextZoom = clampZoom(zoom.value + delta)
	if (nextZoom === zoom.value) return
	zoom.value = nextZoom
	zoomSlider.value = Math.round(nextZoom * 100)
	animateZoomToTarget()
	scheduleRenderAllPages({ delay: 280 })
}

const handleStageWheel = event => {
	const withModifier = event.ctrlKey || event.metaKey
	if (!withModifier) return
	event.preventDefault()
	event.stopPropagation()
	wheelDeltaAcc += event.deltaY
	runWheelZoomLoop()
	// Полная перерисовка только после паузы прокрутки: это заметно снижает "дёргание".
	scheduleRenderAllPages({ delay: 650 })
}

const applyZoomFromSlider = (shouldRender = false) => {
	const nextZoom = clampZoom((Number(zoomSlider.value) || 100) / 100)
	if (nextZoom === zoom.value) return
	zoom.value = nextZoom
	animateZoomToTarget()
	if (shouldRender) {
		scheduleRenderAllPages({ immediate: true })
	}
}

watch(
	() => props.fileUrl,
	() => {
		zoom.value = 1
		displayZoom.value = 1
		renderedZoom.value = 1
		zoomSlider.value = 100
		loadPdf()
	},
	{ immediate: true }
)

onBeforeUnmount(async () => {
	if (renderDebounceTimer) {
		clearTimeout(renderDebounceTimer)
		renderDebounceTimer = null
	}
	stopWheelZoomLoop()
	stopZoomAnimation()
	disconnectObserver()
	await destroyPdf()
})
</script>

<style scoped>
.pdf-viewer {
	flex: 1;
	min-height: 60vh;
	display: flex;
	flex-direction: column;
	gap: 12px;
}

.pdf-viewer__content {
	flex: 1;
	display: flex;
	flex-direction: column;
	gap: 12px;
}

.pdf-viewer__toolbar {
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 8px;
	flex-wrap: wrap;
}

.pdf-viewer__btn {
	border: 1px solid #cbd5e1;
	background: #ffffff;
	color: #0f172a;
	border-radius: 8px;
	height: 30px;
	padding: 0 10px;
	font-size: 14px;
	font-weight: 600;
	cursor: pointer;
}

.pdf-viewer__btn:disabled {
	opacity: 0.45;
	cursor: not-allowed;
}

.pdf-viewer__meta {
	font-size: 13px;
	font-weight: 700;
	color: #0f172a;
}

.pdf-viewer__page-input {
	border: 1px solid #cbd5e1;
	background: #ffffff;
	color: #0f172a;
	border-radius: 8px;
	height: 30px;
	padding: 0 8px;
	width: 88px;
	font-size: 13px;
	font-weight: 600;
}

.pdf-viewer__zoom-slider {
	width: 140px;
	height: 30px;
	cursor: pointer;
}

.pdf-viewer__zoom-slider::-webkit-slider-thumb {
	cursor: pointer;
}

.pdf-viewer__zoom-slider::-moz-range-thumb {
	cursor: pointer;
}

.pdf-viewer__stage {
	position: relative;
	flex: 1;
	min-height: 60vh;
	padding: 16px;
	border-radius: 16px;
	background: #ffffff;
	box-shadow: 0 10px 24px rgba(15, 23, 42, 0.12);
	overflow: auto;
	display: flex;
	align-items: flex-start;
	justify-content: center;
}

.pdf-viewer__pages {
	width: 100%;
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 16px;
}

.pdf-viewer__page {
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 8px;
}

.pdf-viewer__canvas {
	max-width: 100%;
	height: auto;
	background: #ffffff;
	box-shadow: 0 4px 14px rgba(15, 23, 42, 0.12);
}

.pdf-viewer__page-index {
	font-size: 12px;
	color: #64748b;
}

.pdf-viewer__loading {
	color: #475569;
	font-size: 14px;
}

.pdf-viewer__error {
	background: #fef2f2;
	color: #991b1b;
	border: 1px solid #fecaca;
	padding: 12px;
	border-radius: 10px;
}

.pdf-viewer__watermark {
	position: absolute;
	inset: 0;
	pointer-events: none;
	display: flex;
	align-items: center;
	justify-content: center;
	font-size: clamp(20px, 3vw, 42px);
	font-weight: 700;
	letter-spacing: 0.15em;
	text-transform: uppercase;
	color: rgba(15, 23, 42, 0.08);
}
</style>
